import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useBrand } from '@/brand/BrandProvider'
import { apiClient } from '@/api/client'
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Progress } from '@/components/ui/progress'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { PRODUCT, PRODUCT_KEY } from '@/lib/productIdentity'

// ── Types ──────────────────────────────────────────────────────────────────────

interface AccessRequestFormData {
  firm_name: string
  contact_name: string
  email: string
  phone: string
  state: string
  city: string
  deal_volume: string
  engagement_types: string
  how_heard: string
  pricing_pref: string
}

// ── Constants ──────────────────────────────────────────────────────────────────

const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado',
  'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho',
  'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana',
  'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota',
  'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada',
  'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
  'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon',
  'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota',
  'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington',
  'West Virginia', 'Wisconsin', 'Wyoming',
]

const DEAL_VOLUME_OPTIONS = ['1–5 deals/year', '6–15 deals/year', '16+ deals/year']
const ENGAGEMENT_TYPE_OPTIONS = ['Buy-Side QoE', 'Sell-Side QoE', 'Both']
const HOW_HEARD_OPTIONS = ['Referral', 'LinkedIn', 'Google Search', 'Conference/Event', 'Other']
const FLAT_FEE = 'Model B — Flat Fee Per Deal'
const PRICING_OPTIONS = ['Model C — Revenue Share', FLAT_FEE]

// PLAT-REQUEST-ACCESS-1: Valuation sells Flat Fee Per Deal only (pre-selected) and
// has no QoE engagement types. Other products keep the full form.
const FLAT_FEE_ONLY_PRODUCTS: readonly string[] = ['valuation']

const INITIAL_FORM: AccessRequestFormData = {
  firm_name: '', contact_name: '', email: '', phone: '',
  state: '', city: '', deal_volume: '', engagement_types: '',
  how_heard: '', pricing_pref: '',
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function RadioOptions({
  name, options, value, onChange,
}: {
  name: string
  options: string[]
  value: string
  onChange: (val: string) => void
}) {
  return (
    <div className="space-y-2">
      {options.map(opt => (
        <label
          key={opt}
          className="flex items-center gap-3 p-3 rounded-md border border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
        >
          <input
            type="radio"
            name={name}
            value={opt}
            checked={value === opt}
            onChange={e => onChange(e.target.value)}
            className="h-4 w-4 shrink-0"
            style={{ accentColor: 'var(--color-primary)' }}
          />
          <span className="text-sm">{opt}</span>
        </label>
      ))}
    </div>
  )
}

// ── Component ──────────────────────────────────────────────────────────────────

export function RequestAccess() {
  const { theme } = useBrand()
  // PRODUCT-IDENTITY-1 (I3): preserve QoE's exact pre-existing text ("QoE
  // Platform"), same reasoning as TosGate.tsx — see that file's comment.
  const productName = PRODUCT_KEY === 'qoe' ? 'QoE Platform' : PRODUCT.headline
  const [step, setStep] = useState(1)
  const flatFeeOnly = FLAT_FEE_ONLY_PRODUCTS.includes(PRODUCT_KEY)
  const shownPricingOptions = flatFeeOnly ? [FLAT_FEE] : PRICING_OPTIONS
  const asksEngagementTypes = !flatFeeOnly
  const [formData, setFormData] = useState<AccessRequestFormData>(() => (
    flatFeeOnly ? { ...INITIAL_FORM, pricing_pref: FLAT_FEE } : INITIAL_FORM
  ))
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const totalSteps = 3

  const update = (field: keyof AccessRequestFormData, value: string) =>
    setFormData(prev => ({ ...prev, [field]: value }))

  const nextStep = () => setStep(s => Math.min(s + 1, totalSteps))
  const prevStep = () => setStep(s => Math.max(s - 1, 1))

  const handleSubmit = async () => {
    setSubmitting(true)
    setError(null)
    try {
      const res = await apiClient.post('/access-requests', { ...formData, product: PRODUCT_KEY })
      setConfirmationSent(res?.data?.confirmation_sent === true)
      setSubmitted(true)
    } catch {
      setError('Submission failed. Please try again or email info@yansarconsulting.com.')
    } finally {
      setSubmitting(false)
    }
  }

  const isTexas = formData.state.toLowerCase().includes('tex')
  const stepLabel = step === 1 ? 'Firm Info' : step === 2 ? 'Engagement Details' : 'Confirm'

  if (submitted) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: 'var(--color-bg)' }}
      >
        <Card className="w-full max-w-lg shadow-lg">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="text-5xl mb-4">✅</div>
            <h2 className="text-xl font-semibold mb-2">Request Submitted!</h2>
            <p className="text-gray-500 text-sm">
              Thank you, {formData.contact_name}. We'll review your request and be in touch within 1 business day.
            </p>
            <p className="text-gray-400 text-xs mt-4">
              {confirmationSent
                ? `A confirmation has been sent to ${formData.email}.`
                : `We received your request. We'll be in touch at ${formData.email}.`}
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center p-4"
      style={{ backgroundColor: 'var(--color-bg)' }}
    >
      <img
        src={`/api/brand/${theme.key}/logo`}
        alt={theme.name}
        className="h-10 mb-6 object-contain"
        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
      />

      <Card className="w-full max-w-lg shadow-lg">
        <CardHeader>
          <CardTitle style={{ color: 'var(--color-primary)' }}>Request Platform Access</CardTitle>
          <CardDescription>{productName} · CPA Firm Onboarding</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Progress indicator */}
          <div className="mb-8">
            <div className="flex justify-between text-xs text-gray-500 mb-2">
              <span>Step {step} of {totalSteps}</span>
              <span>{stepLabel}</span>
            </div>
            <Progress value={(step / totalSteps) * 100} />
          </div>

          {/* ── Step 1: Firm Information ─────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-gray-700">Firm Information</h3>

              <div className="space-y-1.5">
                <Label>Firm Name *</Label>
                <Input
                  value={formData.firm_name}
                  onChange={e => update('firm_name', e.target.value)}
                  placeholder="Acme CPA LLC"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Contact Name *</Label>
                <Input
                  value={formData.contact_name}
                  onChange={e => update('contact_name', e.target.value)}
                  placeholder="Jane Smith"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={e => update('email', e.target.value)}
                  placeholder="jane@acmecpa.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label>Phone</Label>
                <Input
                  value={formData.phone}
                  onChange={e => update('phone', e.target.value)}
                  placeholder="(555) 000-0000"
                />
              </div>

              <div className="space-y-1.5">
                <Label>State *</Label>
                <Select value={formData.state} onValueChange={v => { if (v) update('state', v) }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent>
                    {US_STATES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>City *</Label>
                <Input
                  value={formData.city}
                  onChange={e => update('city', e.target.value)}
                  placeholder="Dallas"
                />
              </div>

              <Button
                className="w-full mt-2"
                style={{ backgroundColor: 'var(--color-primary)' }}
                disabled={
                  !formData.firm_name || !formData.contact_name ||
                  !formData.email || !formData.state || !formData.city
                }
                onClick={nextStep}
              >
                Next →
              </Button>
            </div>
          )}

          {/* ── Step 2: Engagement Details ───────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-6">
              <h3 className="font-medium text-sm text-gray-700">Engagement Details</h3>

              <div className="space-y-2">
                <Label>Typical deal volume per year *</Label>
                <RadioOptions
                  name="deal_volume"
                  options={DEAL_VOLUME_OPTIONS}
                  value={formData.deal_volume}
                  onChange={v => update('deal_volume', v)}
                />
              </div>

              {asksEngagementTypes && (
                <div className="space-y-2">
                  <Label>Engagement types *</Label>
                  <RadioOptions
                    name="engagement_types"
                    options={ENGAGEMENT_TYPE_OPTIONS}
                    value={formData.engagement_types}
                    onChange={v => update('engagement_types', v)}
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <Label>How did you hear about us?</Label>
                <Select value={formData.how_heard} onValueChange={v => { if (v) update('how_heard', v) }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {HOW_HEARD_OPTIONS.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Preferred pricing model *</Label>
                <Select value={formData.pricing_pref} onValueChange={v => { if (v) update('pricing_pref', v) }}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {shownPricingOptions.map(opt => (
                      <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  ← Back
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  disabled={!formData.deal_volume || (asksEngagementTypes && !formData.engagement_types) || !formData.pricing_pref}
                  onClick={nextStep}
                >
                  Next →
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 3: Confirm & Submit ──────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-medium text-sm text-gray-700">Confirm & Submit</h3>

              {isTexas && (
                <Alert className="border-amber-300 bg-amber-50">
                  <AlertDescription className="text-amber-800 text-sm">
                    <strong>Note:</strong> Texas engagements may be subject to Right of First Refusal provisions.
                    A Yansar representative will reach out to discuss before your account is activated.
                  </AlertDescription>
                </Alert>
              )}

              <Card className="bg-gray-50 border-gray-200">
                <CardContent className="pt-4 space-y-2 text-sm">
                  <div><span className="font-medium text-gray-600">Firm:</span> {formData.firm_name}</div>
                  <div><span className="font-medium text-gray-600">Contact:</span> {formData.contact_name}</div>
                  <div><span className="font-medium text-gray-600">Email:</span> {formData.email}</div>
                  {formData.phone && (
                    <div><span className="font-medium text-gray-600">Phone:</span> {formData.phone}</div>
                  )}
                  <div><span className="font-medium text-gray-600">Location:</span> {formData.city}, {formData.state}</div>
                  <div><span className="font-medium text-gray-600">Deal volume:</span> {formData.deal_volume}</div>
                  {asksEngagementTypes && (
                    <div><span className="font-medium text-gray-600">Engagement types:</span> {formData.engagement_types}</div>
                  )}
                  <div><span className="font-medium text-gray-600">Pricing model:</span> {formData.pricing_pref}</div>
                  {formData.how_heard && (
                    <div><span className="font-medium text-gray-600">Heard via:</span> {formData.how_heard}</div>
                  )}
                </CardContent>
              </Card>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={prevStep} className="flex-1">
                  ← Back
                </Button>
                <Button
                  className="flex-1"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                  onClick={handleSubmit}
                  disabled={submitting}
                >
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400 mt-4">
        Already have an account?{' '}
        <Link to="/login" className="underline hover:text-gray-600">Sign in</Link>
      </p>
    </div>
  )
}
